/* eslint-disable react/jsx-props-no-spreading */

"use client";

import { useSearchParams } from "next/navigation";
import React, { useEffect, useState } from "react";

import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import CreateAccount from "@/components/molecules/policy-document-section/policy-content/create-account-content";
import DocumentContent from "@/components/molecules/policy-document-section/policy-content/document-content";
import FormContent from "@/components/molecules/policy-document-section/policy-content/form-content";
import useFetchPolicyDocData from "@/hooks/api/policy-document/get-policy-doc-data";
import { useLoading } from "@/providers/loading-provider";
import { PolicyDocuments } from "@/types/policy-document-types";
import { CombinedPolicyDocument } from "@/utils/schema/policy-document-page-schema.utils";

import HeaderBanner from "./header-banner/header-banner";

function PolicyDocument({ ...props }: Readonly<CombinedPolicyDocument>) {
  const searchParams = useSearchParams();
  const policyId = searchParams.get("policyId");
  const { showLoading, hideLoading } = useLoading();
  const { policyDocs, isLoading } = useFetchPolicyDocData({ policyId });
  const [policyDocData, setPolicyDocData] = useState<PolicyDocuments[]>([]);
  const [havDoc, setHavDoc] = useState(false);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);

  useEffect(() => {
    if (!policyId && !isFormSubmitted) {
      showLoading();
      setPolicyDocData(policyDocs);
      setHavDoc(true);
      if (!isLoading) {
        hideLoading();
      }
    }
  }, [
    isLoading,
    hideLoading,
    policyDocs,
    policyId,
    showLoading,
    isFormSubmitted,
  ]);

  const handleFormData = (formPolicyDocData: PolicyDocuments[]) => {
    if (!formPolicyDocData.length) {
      return;
    }
    setIsFormSubmitted(true);
    setPolicyDocData(formPolicyDocData);
    setHavDoc(true);
  };

  const { current } = useWizard();

  return (
    <main
      data-testid="policydocument-test-id"
      className=" bg-surface-neutral-option-2 !min-h-[100vh] md:px-spacing-xxl lg:px-spacing-6xl px-spacing-l "
    >
      {/* Top image & thank you header */}
      <div className=" pt-spacing-l md:pt-spacing-xl lg:pt-spacing-xxl flex flex-col gap-spacing-m md:gap-spacing-xl">
        <HeaderBanner {...props.imageBannerArea["0"]} havDoc={havDoc} />
      </div>
      <div className=" flex flex-col gap-spacing-m md:gap-spacing-xl py-spacing-m md:py-spacing-l">
        {havDoc ? (
          // Document content
          <div className="grid lg:grid-cols-12 lg:col-span-12 gap-spacing-l">
            <div className="lg:col-span-7 bg-surface-neutral-option-1 p-spacing-l md:p-spacing-xl rounded-radius-xxl h-fit">
              <DocumentContent
                {...props.documetContainerArea["0"]}
                policyDocData={policyDocData}
              />
            </div>
            {current?.uiElementControls?.showMyAccount && (
              <div className="lg:col-span-5 bg-surface-neutral-option-1 p-spacing-l md:p-spacing-xl rounded-radius-xxl gap-spacing-l h-fit">
                <CreateAccount {...props.myAccountArea["0"]} />
              </div>
            )}
          </div>
        ) : (
          // SignIn Form
          <div className="grid lg:grid-cols-12 lg:col-span-12 gap-spacing-l">
            <div className="col-span-12 lg:col-span-7 bg-surface-neutral-option-1 p-spacing-l md:p-spacing-xl rounded-radius-xxl">
              <FormContent
                {...props.questionArea["0"]}
                setPolicyDocData={handleFormData}
                showPostCode={current?.uiElementControls?.showPostCode}
                showPhoneNumber={current?.uiElementControls?.showPhoneNumber}
                phoneNumberLength={
                  current?.brandSpecificParams?.phoneNumberLength as number
                }
                phoneNumberRegex={
                  current?.brandSpecificParams?.phoneNumberRegex as string
                }
                postCodeMinLength={
                  current?.brandSpecificParams?.postCodeMinLength as number
                }
                postCodeMaxLength={
                  current?.brandSpecificParams?.postCodeMaxLength as number
                }
                postCodeRegex={
                  current?.brandSpecificParams?.postCodeRegex as string
                }
              />
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
export default PolicyDocument;
